import { useEffect } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@shared/lib/cn'
import {
  APP_LANG_OPTIONS,
  APP_SUPPORTED_LOCALES,
  BROWSER_LANG_TO_LOCALE,
  LOCALES,
  type AppLocale,
} from '@shared/lib/locale'

function normalize(lang: string): AppLocale {
  const base = lang.split('-')[0]
  const mapped = (BROWSER_LANG_TO_LOCALE[base] ?? base) as AppLocale
  return APP_SUPPORTED_LOCALES.includes(mapped) ? mapped : LOCALES.ru
}

export interface LanguageSelectorOption {
  value: string
  label: string
  flagCountry: string
}

interface LanguageSelectorProps {
  className?: string
  /**
   * Controlled mode: pass value + onValueChange + options to use the selector
   * as a generic language dropdown (e.g. a content-language filter) instead of
   * the app-language switcher. All three must be provided together.
   */
  value?: string
  onValueChange?: (value: string) => void
  options?: LanguageSelectorOption[]
}

export function LanguageSelector({
  className,
  value,
  onValueChange,
  options,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation()
  const isControlled = value !== undefined && onValueChange !== undefined && options !== undefined

  const detected = i18n.language
  const appLang = normalize(detected)

  useEffect(() => {
    if (!isControlled && detected !== appLang) {
      i18n.changeLanguage(appLang)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const opts = isControlled ? options : APP_LANG_OPTIONS
  const selected = isControlled ? value : appLang
  const current = opts.find((o) => o.value === selected) ?? opts[0]

  const handleChange = async (lang: string) => {
    if (isControlled) {
      onValueChange(lang)
      return
    }
    // Preload the target language's in-use namespaces before switching so the
    // tree doesn't suspend to the root spinner mid-swap.
    await i18n.loadLanguages(lang)
    await i18n.changeLanguage(lang)
  }

  return (
    <SelectPrimitive.Root value={selected} onValueChange={handleChange}>
      <SelectPrimitive.Trigger
        className={cn('flex cursor-pointer items-center justify-between gap-2', className)}
      >
        <span className="flex items-center gap-2">
          <img
            src={`https://flagcdn.com/w40/${current.flagCountry}.png`}
            alt={current.label}
            className="h-4 w-6 rounded-sm object-cover"
          />
          <span className="text-sm">{current.label}</span>
        </span>
        <SelectPrimitive.Icon>
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className="border-gray-border animate-in fade-in-0 zoom-in-95 z-50 min-w-32 overflow-hidden rounded-xl border bg-white shadow-lg"
          position="popper"
          sideOffset={4}
        >
          <SelectPrimitive.Viewport className="p-1">
            {opts.map((opt) => (
              <SelectPrimitive.Item
                key={opt.value}
                value={opt.value}
                className={cn(
                  'relative flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm outline-none select-none',
                  'hover:bg-orange-primary/10 focus:bg-orange-primary/10',
                  'data-[state=checked]:text-orange-primary',
                  'data-disabled:pointer-events-none data-disabled:opacity-50'
                )}
              >
                <img
                  src={`https://flagcdn.com/w40/${opt.flagCountry}.png`}
                  alt={opt.label}
                  className="h-4 w-6 rounded-sm object-cover"
                />
                <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
